/**
 * FeatureSuggestionModal - Modal for suggesting new features
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useMicrosoftAuth } from '@/hooks/useMicrosoftAuth';
import { useCreateFeatureSuggestion } from '@/hooks/useFeedback';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import {
  X,
  Lightbulb,
  Puzzle,
  Zap,
  BarChart3,
  Palette,
  MoreHorizontal,
  Check,
  Loader2,
} from 'lucide-react';
import type { FeatureSuggestionCategory, FeatureSuggestionPriority } from '@/types/feedback';

const CATEGORIES = [
  { value: 'feature' as FeatureSuggestionCategory, label: 'New Feature', icon: Lightbulb },
  { value: 'integration' as FeatureSuggestionCategory, label: 'Integration', icon: Puzzle },
  { value: 'automation' as FeatureSuggestionCategory, label: 'Automation', icon: Zap },
  { value: 'reporting' as FeatureSuggestionCategory, label: 'Reporting', icon: BarChart3 },
  { value: 'ui_ux' as FeatureSuggestionCategory, label: 'UI/UX', icon: Palette },
  { value: 'other' as FeatureSuggestionCategory, label: 'Other', icon: MoreHorizontal },
];

const PRIORITIES = [
  { value: 'low' as FeatureSuggestionPriority, label: 'Nice to have', color: 'bg-gray-100 text-gray-700' },
  { value: 'medium' as FeatureSuggestionPriority, label: 'Would be helpful', color: 'bg-blue-100 text-blue-700' },
  { value: 'high' as FeatureSuggestionPriority, label: 'Important', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'critical' as FeatureSuggestionPriority, label: 'Critical need', color: 'bg-red-100 text-red-700' },
];

interface FeatureSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FeatureSuggestionModal({ isOpen, onClose }: FeatureSuggestionModalProps) {
  const { user, profile } = useAuth();
  const { user: msUser } = useMicrosoftAuth();
  const createSuggestion = useCreateFeatureSuggestion();

  // Resolve user from either Supabase or Microsoft SSO
  const currentUserId = user?.id || msUser?.email || '';
  const currentUserName = profile?.full_name || msUser?.name || undefined;
  const currentUserEmail = user?.email || msUser?.email || undefined;
  const isSignedIn = !!user || !!msUser;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<FeatureSuggestionCategory>('feature');
  const [priority, setPriority] = useState<FeatureSuggestionPriority>('medium');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setCategory('feature');
      setPriority('medium');
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim() || !isSignedIn) return;

    try {
      await createSuggestion.mutateAsync({
        formData: {
          title: title.trim(),
          description: description.trim(),
          category,
          priority,
        },
        user: {
          id: currentUserId,
          name: currentUserName,
          email: currentUserEmail,
        },
      });

      onClose();
    } catch (error) {
      console.error('Failed to submit suggestion:', error);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <Card className="relative w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-primary-50 dark:border-gray-700 dark:bg-primary-900/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg dark:bg-primary-900/40">
              <Lightbulb className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Suggest a Feature</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Help shape the future of Flow</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-8rem)]">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Feature Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Add bulk property import from CSV"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-[3px] focus:ring-primary-500/20 focus:border-primary-500"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <div className="grid grid-cols-3 gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setCategory(cat.value)}
                  className={cn(
                    'flex flex-col items-center gap-1 p-3 border rounded-lg transition-colors text-center',
                    category === cat.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:bg-gray-50'
                  )}
                >
                  <cat.icon className="h-5 w-5" />
                  <span className="text-xs font-medium">{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the feature you'd like to see. What problem does it solve? How would you use it?"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-[3px] focus:ring-primary-500/20 focus:border-primary-500 resize-none"
            />
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How important is this to you?
            </label>
            <div className="flex flex-wrap gap-2">
              {PRIORITIES.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPriority(p.value)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                    priority === p.value
                      ? cn(p.color, 'ring-2 ring-offset-1 ring-gray-400')
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!title.trim() || !description.trim() || createSuggestion.isPending}
            className="flex-1"
          >
            {createSuggestion.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                Submit Suggestion
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>,
    document.body
  );
}
